<template>
  <div class="card">
    <h4>{{ $t("app.company.info.title") }}</h4>
    <p>{{ $t("app.company.info.desc") }}</p>
    <Form v-if="state.formValues" class="form-container" @submit="edit" v-slot="{ errors, meta, isSubmitting }">
      <div class="input-inline">
        <VField
          :placeholder="$t('app.company.info.companyPlaceholder')"
          name="company"
          rules="required|min:2|max:50"
          v-model="state.formValues.details.name"
          :label="$t('app.form.register.company')"
          :class="{
            invalid: Object.keys(errors)[0] === 'company',
          }"
        />
        <VField :placeholder="$t('app.company.info.vatPlaceholder')" name="vat_number" v-model="state.formValues.details.vat_id" rules="required|vat" :label="$t('app.form.register.vat_number')" :class="{ invalid: Object.keys(errors)[0] === 'vat_number' }" />
      </div>
      <div class="input-inline">
        <VField :placeholder="$t('app.company.info.addressPlaceholder')" name="address" v-model="state.formValues.details.address" rules="required|min:2|max:256" :label="$t('app.form.register.address')" :class="{ invalid: Object.keys(errors)[0] === 'address' }" />
        <VField :placeholder="$t('app.company.info.cityPlaceholder')" name="city" v-model="state.formValues.details.city" rules="required|textOnly|min:2|max:128" :label="$t('app.form.register.city')" :class="{ invalid: Object.keys(errors)[0] === 'city' }" />
      </div>
      <div class="input-inline">
        <VField :placeholder="$t('app.company.info.postalCodePlaceholder')" name="postalCode" v-model="state.formValues.details.postal_code" rules="required|postalCode" :label="$t('app.form.register.postalCode')" :class="{ invalid: Object.keys(errors)[0] === 'postalCode' }" />

        <div class="input-wrapper">
          <label v-text="$t('app.form.register.country')" for="country" />
          <v-select id="country" name="country" :placeholder="$t('app.form.register.selectCountry')" v-model="state.formValues.details.country" :clearable="false" :required="true" :options="['France', 'Belgique', 'Luxembourg']">
            <template #search="{ attributes, events }">
              <input class="vs__search" :required="!state.formValues.details.country" v-bind="attributes" v-on="events" />
            </template>
            <template v-slot:no-options="{ search, searching }">
              <div style="padding: 8px 0">{{ $t("app.company.info.noResult") }}{{ searching ? ` ${search}` : "" }}.</div>
            </template>
          </v-select>
        </div>
      </div>
      <div class="input-inline">
        <div v-if="Object.keys(errors).length > 0" class="form-error" v-text="Object.values(errors)[0]" />
        <Button class="primary" :label="$t('app.company.info.saveChanges')" :type="!isDisabled(meta.valid) && !isSubmitting ? 'submit' : 'button'" :loading="isSubmitting" :disabled="isDisabled(meta.valid)" />
      </div>
    </Form>
  </div>
</template>

<script setup lang="ts">
import { Form } from "vee-validate";
import { reactive, computed } from "vue";
import { useRoute } from "vue-router";
import { useToast } from "vue-toastification";

import { getError } from "@/composables/error.js";
import { useEditOrganisation, useOrganisation } from "@/queries/organisation.query";
import { Organisation } from "@/types/company";

const toast = useToast();
const route = useRoute();

const { data: organisation, isPending: isOrganisationPending } = useOrganisation();
const { mutate: editOrganisation } = useEditOrganisation();

const state = reactive({
  formValues: null as Organisation,
});

const valuesAreModified = computed(() => {
  return JSON.stringify(state.formValues) !== JSON.stringify(organisation.value);
});

function isDisabled(isValid) {
  return !isValid || !state.formValues.details.country || !valuesAreModified.value;
}

function edit() {
  editOrganisation(state.formValues, {
    onSuccess: () => {
      // TODO : localize this message
      toast.success("Les informations de votre organisation ont bien été modifiées.");
    },
    onError: (error) => {
      toast.error(getError(error));
    }
  });
}

watch(organisation, () => {
  if (!isOrganisationPending.value) {
    state.formValues = { ...organisation.value };
  }
});

onMounted(() => {
  if (!isOrganisationPending.value) {
    state.formValues = { ...organisation.value };
  }
});

</script>
